import type { Me, Permission } from '@hbac/contract';
import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from 'react';
import { clearCachedAppData } from '../lib/app-data-cache';
import { forgetTasks } from '../lib/task-store';

const STORAGE_KEY = 'hbac-auth';

interface StoredAuth {
  token: string;
  member: Me;
}

export interface AuthValue {
  token: string | null;
  member: Me | null;
  /**
   * Whether to SHOW something that needs this permission. Convenience only:
   * the server checks every call on its own. Fails closed, including for a
   * profile stored before roles existed, which has no permission list.
   */
  can: (permission: Permission) => boolean;
  /** Holds the built-in administrator role. For labels, never for access. */
  isAdministrator: boolean;
  /** Left the club: can settle old bills, cannot start new ones. The server enforces it too. */
  isAlumni: boolean;
  login: (token: string, member: Me) => void;
  logout: () => void;
  setMember: (member: Me) => void;
  /**
   * Rotate only the session token. The server re-issues one after a password
   * change (which revokes the old token version), so the current session
   * survives; the profile is unchanged.
   */
  setToken: (token: string) => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * Reads the token outside React.
 *
 * The HTTP transport needs it on every request but is constructed before any
 * component mounts, so it cannot use the hook. Reading from storage each time
 * also means a token rotated by another tab is picked up immediately.
 */
export function readStoredToken(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? ((JSON.parse(raw) as StoredAuth).token ?? null) : null;
  } catch {
    return null;
  }
}

function persist(next: StoredAuth | null): void {
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Blocked storage: the session stays in memory for this tab only.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<StoredAuth | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as StoredAuth) : null;
    } catch {
      return null;
    }
  });

  const login = useCallback((token: string, member: Me) => {
    forgetTasks(member.memberId); // another member's remembered board never shows to this one
    const next = { token, member };
    setAuth(next);
    persist(next);
  }, []);

  const logout = useCallback(() => {
    setAuth(null);
    clearCachedAppData(); // never leave member emails / bank numbers on a shared device
    forgetTasks();
    persist(null);
  }, []);

  const setMember = useCallback((member: Me) => {
    setAuth((prev) => {
      if (!prev) return prev;
      const next = { ...prev, member };
      persist(next);
      return next;
    });
  }, []);

  const setToken = useCallback((token: string) => {
    if (!token) return;
    setAuth((prev) => {
      if (!prev) return prev;
      const next = { ...prev, token };
      persist(next);
      return next;
    });
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      token: auth?.token ?? null,
      member: auth?.member ?? null,
      can: (permission: Permission) => Boolean(auth?.member?.permissions?.includes(permission)),
      isAdministrator: Boolean(auth?.member?.roles?.some((r) => r.isSystem)),
      isAlumni: auth?.member?.status === 'alumni',
      login,
      logout,
      setMember,
      setToken,
    }),
    [auth, login, logout, setMember, setToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
